import Log, {Errors} from "@khan/logging";

import {
    PROGRAM_STATUS,
    type ProgramStatus,
} from "#scratchpad-shared-package/types.ts";
import Backbone, {
    type Backbone_Attrs,
    type ModelOpts,
} from "#shared-package/backbone-lite.js";

import {LegacyRequireIfLoaded} from "./legacy-require-if-loaded.ts";

type ScratchpadRevisionModel = BackboneModel<
    Backbone.Model,
    {
        parse: (resp: any) => any;
        hasAudio: () => boolean;
        getMp3Url: () => string;
    },
    Empty
>;

export const ScratchpadRevision: ScratchpadRevisionModel =
    Backbone.Model.extend(
        {
            parse: function (resp: any) {
                // resp.created comes down from the server as an RFC date string (see
                // api/jsonify.py). We want it as a native JS Date object.
                resp.created = new Date(resp.created);
                // Some user reports came in that indicated the date isn't always
                // correct here, so check for an invalid date.
                // Date objects are based on a time value that is the number of
                // milliseconds since 1 January, 1970 UTC, so they can be converted
                // to a Number.
                if (isNaN(Number(resp.created))) {
                    resp.created = new Date();
                }
                return resp;
            },
            hasAudio: function (this: any) {
                return !!this.getMp3Url();
            },
            getMp3Url: function (this: any) {
                const mp3Url =
                    this.get("translatedMp3Url") || this.get("mp3Url");
                return mp3Url;
                // NOTE(pamela): We are disabling this for now, as varnish has
                //  some issues with chunked transfer encoding and occasionally
                //  shows a ERR_CONTENT_LENGTH_MISMATCH with large files
                /*if (mp3Url) {
            return mp3Url.replace(
                "ka-cs-scratchpad-audio.s3.amazonaws.com",
                "kasandbox.org/s3/ka-cs-scratchpad-audio"
                );
        }
        return null;
        */
            },
        },
        // TypeScript requires classProperties to properly type check Backbone.Models
        {},
    );

export type UserScratchpadModel = BackboneModel<
    Backbone.Model,
    {
        url: () => string;
        defaults: {
            challengeStatus: ProgramStatus;
            stashedCode: string;
        };
        initialize: () => void;
        saveChallengeStatus: (
            status: ProgramStatus,
            options: ModelOpts,
        ) => void;
    },
    Empty
>;

export const UserScratchpad: UserScratchpadModel = Backbone.Model.extend(
    {
        url: function () {
            Log.error(
                "UserScratchpads must be fetched/saved via GraphQL!",
                Errors.Internal,
            );

            return "";
        },

        // These should match UserScratchpad defaults in models.py where needed
        // so that phantom users behave properly.
        defaults: {
            challengeStatus: PROGRAM_STATUS.CHALLENGE_NOT_STARTED,
            stashedCode: "",
        },

        initialize: function (this: any) {
            // Total number of significant inactivity (no-coding) seconds recorded
            this.inactivitySeconds = 0;
            // Most recent coding activity (editor change)
            this.lastActivityTime = null;
        },

        saveChallengeStatus: function (
            this: any,
            status: ProgramStatus,
            options: ModelOpts,
        ) {
            if (!Object.values(PROGRAM_STATUS).includes(status)) {
                Log.error("Invalid status: " + status, Errors.Internal);
                return;
            }
            const attrs: Record<string, any> = {};

            // Save status: 1 if complete, 0 if started as per scratchpads/models.py
            attrs.challengeStatus = status;

            // Do a partial save, to avoid overriding values saved elsewhere
            options = options || {};

            // Setting options.attrs sends only these attributes,
            //  emulating PATCH, which is currently breaking in FF
            // TODO(pamela): Set options.attr to options.patch = true when fixed
            // TODO(pamela): Remove scratchpadId once PATCH is fixed,
            //  we need to also explicitly send it until then.
            attrs["scratchpadId"] = this.get("scratchpadId");
            attrs["scratchpadKind"] = this.get("scratchpadKind");
            options.attrs = attrs;
            this.save(attrs, options);
        },
    },
    // TypeScript requires classProperties to properly type check Backbone.Models
    {},
);

export type ScratchpadModel = BackboneModel<
    Backbone.Model,
    {
        urlRoot: string;
        url: () => string;
        pathForShow: () => string;
        pathForImage: () => string;
        urlForShow: () => string;
        urlForImage: () => string;
        getVersion: () => string | 0;
        setVersion: (version: string) => void;
        getCode: () => string;
        getFolds: () => string;
        getPlayback: () => string;
        getYoutubeId: () => string;
        setYoutubeId: (youtubeId: string) => void;
        getYoutubeUrl: () => string;
        isResizableType: () => boolean;
        toJSONForTemplate: () => {
            id: string;
            imagePath: string;
            showPath: string;
            title: string;
            translatedTitle: string;
        };
        parse: (resp: any, xhr: XMLHttpRequest) => any;
        // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
        fork: () => Scratchpad;
        save: (attributes: Backbone_Attrs, options: ModelOpts) => any;
        currentRevision: () => string;
        isTalkie: () => boolean;
        isChallenge: () => boolean;
        isProject: () => boolean;
        isOfficial: () => boolean;
        isCategorySet: () => boolean;
        isProjectOrFork: () => boolean;
        hasObjectives: () => boolean;
        isOwner: () => boolean;
        inTutorial: () => boolean;
        isOriginProject: () => boolean;
        setTitle: (title: string) => void;
    },
    Empty
>;

export const Scratchpad: ScratchpadModel = Backbone.Model.extend({
    url: function () {
        Log.error(
            "Scratchpads must be fetched/saved via GraphQL!",
            Errors.Internal,
        );

        return "";
    },

    pathForShow: function (this: any) {
        return this.get("relativeUrl");
    },

    pathForImage: function (this: any) {
        return this.get("imagePath");
    },

    urlForShow: function (this: any) {
        return this.get("url");
    },

    urlForImage: function (this: any) {
        return this.get("imageUrl");
    },

    // Get the version associated with the current scratchpad if one exists.
    // If a Scratchpad exists but no configVersion exists then
    // this Scratchpad is from before we added the versions
    // (and, is thus, at version 0)
    getVersion: function (this: any) {
        if (this.isNew()) {
            const ScratchpadUIModule = LegacyRequireIfLoaded.ScratchpadUIModule;
            if (
                ScratchpadUIModule &&
                ScratchpadUIModule.ScratchpadUI &&
                ScratchpadUIModule.ScratchpadUI.liveEditor
            ) {
                return ScratchpadUIModule.ScratchpadUI.liveEditor.config.latestVersion();
            }
        }
        return this.currentRevision().get("configVersion") || 0;
    },

    setVersion: function (this: any, version: string) {
        this.currentRevision().set("configVersion", version);
    },

    getCode: function (this: any) {
        const contentKind = this.get("contentKind");

        // NOTE (josh): Scratchpads shouldn't have an undefined contentKind,
        // but in certain rare cases (eg. /new/webpage), this is the case.
        // The underlying issue remains unsolved, but this band-aid fixes
        // that scratchpad, as the code is stored on the revision.
        if (contentKind === "Scratchpad" || !contentKind) {
            return this.currentRevision().get("code");
        }
        // Newer program kinds, e.g. Talkthroughs, store everything on the
        // model.
        return this.get("code");
    },

    getFolds: function (this: any) {
        if (this.get("contentKind") === "Scratchpad") {
            return this.currentRevision().get("folds");
        }
        // Newer program kinds, e.g. Talkthroughs, store everything on the
        // model
        return this.get("folds");
    },

    getPlayback: function (this: any) {
        if (this.get("contentKind") === "Scratchpad") {
            return this.currentRevision().get("playback");
        }
        // Newer program kinds, e.g. Talkthroughs, store everything on the
        // model
        return this.get("playback");
    },

    // Scratchpad revisions have Youtube IDs that are used for subtitles
    // Note that this is different from the youtubeId property on a Scratchpad
    // which used to be used for displaying Youtube videos next to a Scratchpad
    getYoutubeId: function (this: any): string {
        if (this.get("contentKind") === "Scratchpad") {
            return this.currentRevision().get("youtubeId");
        }
        // Newer program kinds, e.g. Talkthroughs, store everything on the
        // model
        return this.get("youtubeId");
    },

    setYoutubeId: function (this: any, youtubeId: string) {
        this.currentRevision().set("youtubeId", youtubeId);
    },

    getYoutubeUrl: function (this: any) {
        return `https://www.youtube.com/embed/${this.getYoutubeId()}`;
    },

    isResizableType: function (this: any) {
        return (
            this.get("userAuthoredContentType") === "webpage" ||
            this.get("userAuthoredContentType") === "sql"
        );
    },

    toJSONForTemplate: function (this: any) {
        return {
            id: this.get("id"),
            imagePath: this.pathForImage(),
            showPath: this.pathForShow(),
            title: this.get("title"),
            translatedTitle: this.get("translatedTitle"),
        };
    },

    // This is called whenever data is pulled down via fetch or save.
    //
    // We want to add a revision key with another backbone model (since backbone
    // doesn't support nested .set and .get out of the box
    parse: function (resp: any, xhr: XMLHttpRequest) {
        resp.revision = new ScratchpadRevision(resp.revision, {
            parse: true,
        });
        resp.trustedRevision = new ScratchpadRevision(resp.trustedRevision, {
            parse: true,
        });
        return resp;
    },

    // Returns a copy of the Scratchpad with originScratchpadId and
    // originRevisionId set
    fork: function (this: any) {
        const currentRev = this.currentRevision();
        const forkedScratchpad = new Scratchpad({
            title: this.get("title"),
            revision: currentRev.clone().unset("id").set({
                mp3Url: "",
                playback: "",
                tests: "",
            }),
            originScratchpadId: this.get("id"),
            originScratchpadKind: this.get("contentKind"),
            originRevisionId: currentRev.get("id"),
        });

        return forkedScratchpad;
    },

    save: function (this: any, attributes: Backbone_Attrs, options: ModelOpts) {
        this.get("revision")
            .unset("created")
            .unset("kind")
            .unset("scratchpadId")
            .unset("id")
            .unset("hasAudio");

        return Scratchpad.__super__.save.call(this, attributes, options);
    },

    // Sometimes we put non-KA-created programs in curriculum,
    //  but to be safer about that, we set a trusted revision on them,
    //  one that we know is good.
    // We pass that down for viewing as long as the program is embedded,
    //  has a trusted revision, and is not being viewed by its author.
    currentRevision: function (this: any) {
        // NOTE(jeresig): We need to check for ScratchpadUI here as this
        // can be called inside a discussion context (loading scratchpad
        // screenshots) in which there is no ScratchpadUI object.
        const ScratchpadUIModule = LegacyRequireIfLoaded.ScratchpadUIModule;
        if (ScratchpadUIModule && !this.isOwner()) {
            const ScratchpadUI = ScratchpadUIModule.ScratchpadUI;
            const trustedRevision = this.get("trustedRevision");
            if (
                ScratchpadUI.trusted &&
                trustedRevision &&
                trustedRevision.get("id")
            ) {
                return this.get("trustedRevision");
            }
        }

        return this.get("revision");
    },

    isTalkie: function (this: any) {
        return !!(
            this.get("contentKind") === "Talkthrough" ||
            (this.currentRevision() && this.currentRevision().get("hasAudio"))
        );
    },

    isChallenge: function (this: any) {
        return (
            this.get("contentKind") === "Challenge" ||
            this.get("category") === "challenge"
        );
    },

    isProject: function (this: any) {
        return (
            this.get("contentKind") === "Project" ||
            this.get("category") === "project"
        );
    },

    isOfficial: function (this: any) {
        return (
            this.get("contentKind") === "Interactive" ||
            this.get("category") === "official"
        );
    },

    isCategorySet: function (this: any) {
        return !!this.get("category");
    },

    isProjectOrFork: function (this: any) {
        return this.isProject() || this.isOriginProject();
    },

    hasObjectives: function (this: any) {
        return this.isChallenge() || this.isProject();
    },

    isOwner: function (this: any) {
        const ScratchpadUIModule = LegacyRequireIfLoaded.ScratchpadUIModule;
        return (
            ScratchpadUIModule &&
            ScratchpadUIModule.ScratchpadUI &&
            ScratchpadUIModule.ScratchpadUI.currentUserKaid === this.get("kaid")
        );
    },

    inTutorial: function (this: any) {
        return !!this.get("defaultUrlPath");
    },

    isOriginProject: function (this: any) {
        return this.get("originIsProject");
    },

    // Use this to set the title of the program - see note for getShowTitle.
    //
    // It's important to note that setting translatedTitle client side will
    // not have an effect when saved - our cs api only records title,
    // translatedTitle comes from the work of translators. It is important to
    // keep them in sync here - in some places we use translatedTitle so it
    // needs to be set.
    setTitle: function (this: any, title: string) {
        this.set("title", title);
        this.set("translatedTitle", title);
    },
});
